import React from 'react';
import { formatFieldName, formatValue } from '../../utils/formatters';
import SummaryCard from '../shared/SummaryCard';

const CrawlStatisticsSection = ({ crawlStatistics, onViewWebsiteGraph, hasWebsiteGraph }) => {
    if (!crawlStatistics || typeof crawlStatistics !== 'object') {
        return null;
    }

    // Format date values
    const formatDate = (dateString) => {
        if (!dateString) return '—';
        try {
            const date = new Date(dateString);
            return date.toLocaleString();
        } catch (error) {
            return formatValue(dateString);
        }
    };

    // Calculate duration if both dates are available
    const getDuration = () => {
        if (crawlStatistics.crawl_started_at && crawlStatistics.crawl_completed_at) {
            try {
                const start = new Date(crawlStatistics.crawl_started_at);
                const end = new Date(crawlStatistics.crawl_completed_at);
                const diffMs = end - start;
                const diffSeconds = Math.floor(diffMs / 1000);
                const minutes = Math.floor(diffSeconds / 60);
                const seconds = diffSeconds % 60;
                if (minutes > 0) {
                    return `${minutes}m ${seconds}s`;
                }
                return `${seconds}s`;
            } catch (error) {
                return '—';
            }
        }
        return '—';
    };

    return (
        <div style={{ marginBottom: '30px' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
            }}>
                <h4 style={{ color: '#e0e0e0', fontWeight: '600', margin: 0 }}>Crawl Statistics</h4>
                <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                }}>
                    {hasWebsiteGraph && onViewWebsiteGraph && (
                        <button
                            onClick={onViewWebsiteGraph}
                            style={{
                                backgroundColor: '#3a3a3a',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px 14px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#4a4a4a'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#3a3a3a'}
                        >
                            View Website Graph
                        </button>
                    )}
                </div>
            </div>
            <div style={{
                backgroundColor: '#2d2d2d',
                border: '1px solid #404040',
                borderRadius: '6px',
                padding: '20px'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px'
                }}>
                    {crawlStatistics.total_pages !== undefined && (
                        <SummaryCard label="Total Pages" value={formatValue(crawlStatistics.total_pages)} />
                    )}
                    {crawlStatistics.total_pages_crawled !== undefined && (
                        <SummaryCard label="Pages Crawled" value={formatValue(crawlStatistics.total_pages_crawled)} />
                    )}
                    {crawlStatistics.total_images !== undefined && (
                        <SummaryCard label="Total Images" value={formatValue(crawlStatistics.total_images)} />
                    )}
                    {crawlStatistics.total_css !== undefined && (
                        <SummaryCard label="Total CSS" value={formatValue(crawlStatistics.total_css)} />
                    )}
                    {crawlStatistics.total_js !== undefined && (
                        <SummaryCard label="Total JS" value={formatValue(crawlStatistics.total_js)} />
                    )}
                    {crawlStatistics.total_internal_links !== undefined && (
                        <SummaryCard label="Internal Links" value={formatValue(crawlStatistics.total_internal_links)} />
                    )}
                    {crawlStatistics.total_external_links !== undefined && (
                        <SummaryCard label="External Links" value={formatValue(crawlStatistics.total_external_links)} />
                    )}
                    {crawlStatistics.crawl_depth !== undefined && (
                        <SummaryCard label="Crawl Depth" value={formatValue(crawlStatistics.crawl_depth)} />
                    )}
                </div>
                
                {(crawlStatistics.crawl_started_at || crawlStatistics.crawl_completed_at) && (
                    <div style={{
                        marginTop: '20px',
                        paddingTop: '20px',
                        borderTop: '1px solid #404040'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '15px'
                        }}>
                            {crawlStatistics.crawl_started_at && (
                                <div style={{
                                    backgroundColor: '#262626',
                                    border: '1px solid #373737',
                                    borderRadius: '6px',
                                    padding: '16px'
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Crawl Started
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#e0e0e0',
                                        fontWeight: '500'
                                    }}>
                                        {formatDate(crawlStatistics.crawl_started_at)}
                                    </div>
                                </div>
                            )}
                            {crawlStatistics.crawl_completed_at && (
                                <div style={{
                                    backgroundColor: '#262626',
                                    border: '1px solid #373737',
                                    borderRadius: '6px',
                                    padding: '16px'
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Crawl Completed
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#e0e0e0',
                                        fontWeight: '500'
                                    }}>
                                        {formatDate(crawlStatistics.crawl_completed_at)}
                                    </div>
                                </div>
                            )}
                            {(crawlStatistics.crawl_started_at && crawlStatistics.crawl_completed_at) && (
                                <div style={{
                                    backgroundColor: '#262626',
                                    border: '1px solid #373737',
                                    borderRadius: '6px',
                                    padding: '16px'
                                }}>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#909090',
                                        textTransform: 'uppercase',
                                        marginBottom: '8px',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Duration
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#e0e0e0',
                                        fontWeight: '500'
                                    }}>
                                        {getDuration()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CrawlStatisticsSection;


